import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { loadable } from '@Components/Utilities/SandhillsLoadable';
import { Fragment } from 'react';
import { chunkArray, chunkByBreakingIndexes, toSlug } from '@Components/Utilities/SandhillsUtility';
import { sandhillsRequest } from '@Components/Utilities/SandhillsUtility';
import { useState, useEffect } from 'react';
import TranslationText from '@Components/Utilities/TranslationText';

const CircularProgress = loadable(() => import('@mui/material/CircularProgress'));

const link = (isParent, useNormalFontForParents, cat) => {
    return (
        <a className='accordion__category-link' href={cat.RawURL}>
            {isParent ?
                (useNormalFontForParents ?
                    <span>{cat.Name}</span> :
                    <b>{cat.Name }</b>) :
                <span>{cat.Name}</span>}
        </a>
    );
}

const createDrilldown = (key, fullCategoryList, numColumns, removeChildren, useNormalFontForParents) => {
    if (!numColumns) numColumns = 1;
    var list = [];

    let colBreakIndexes = fullCategoryList.map((e, i) => e.isColumnBreaker ? i : undefined).filter(x => x);
    if (colBreakIndexes.length > 0) {
        list = chunkByBreakingIndexes(fullCategoryList, colBreakIndexes);
    }

    if (list.length <= 0) {
        list = chunkArray(fullCategoryList, numColumns, removeChildren);
    }

    return list.map((catlist, idx) => {
        return (
            <Fragment {...(key ? { key: `${key}-list-${idx}-${catlist.length}` } : {})}>
                {createDrilldownLinks(`${key}-list${idx}-ul`, catlist, useNormalFontForParents)}
            </Fragment>
        );
    });
};


const createDrilldownLinks = (key, categoryList, useNormalFontForParents) => {
    return categoryList.map((cat, idx) => {
        let isParent = cat.Children?.length > 0;
        let numColumns = isParent ? cat.Columns : 0;
        let children = chunkArray(cat.Children || [], numColumns, null);
        return (
            <Fragment {...(key ? { key: `${key}-cat-${idx}` } : {})}>
                {isParent ?
                    <li>
                        <ul className='accordion__category-list' key={`accordion-ul-${cat.Name}-${idx}`}>
                            <li className='accordion__category-parent' key={`li-sub-list-${cat.Name}-${idx}`}>
                                {link(isParent, useNormalFontForParents, cat)}
                            </li>
                            <li>
                                <ul className='accordion__category-sub-list' key={`ul-sub-list-${cat.Name}-${idx}`}>
                                    {children.map(child => createDrilldown(`${key}-dd-div`, child, 1))}
                                </ul>
                            </li>
                        </ul>
                    </li>
                    :
                    <li key={`li-${cat.Name}-i`} className='accordion__category-list-item'>
                        {link(isParent, useNormalFontForParents, cat)}
                        {isParent && children.map(child => createDrilldown(`${key}-dd-li`, child, 1, true, false))}
                    </li>}
            </Fragment>
        );
    });
};

const SandhillsAccordion = (props) => {
    const [cats, setCats] = useState(props.categories);
    const [isLoading, setIsLoading] = useState(false);

    const lazyLoadItems = (url) => {
        sandhillsRequest({
            url: url,
            method: 'post',
            errorHandling: {
                showDetails: false,
                useConsole: false
            }
        }).then((response) => {
            setCats(response);
        }).catch((error) => {
            console.log('Request canceled:', error.message);
        });
    };

    useEffect(() => {
        setIsLoading(false);
    }, [cats]);

    useEffect(() => {
        // Load data on mount if defaultExpanded is true and AjaxLoadURL is provided
        if (props.defaultExpanded && props.AjaxLoadURL && !cats?.length > 0) {
            setIsLoading(true);
            lazyLoadItems(props.AjaxLoadURL);
        }
    }, [props.defaultExpanded, props.AjaxLoadURL]);

    const accordionId = props?.isRentalAccordion && !props.accordionTitle?.includes("For Rent") && !props.accordionTitle?.includes("For Lease") ? `panel-${toSlug(props.accordionTitle)}-for-rent-header` : `panel-${toSlug(props.accordionTitle)}-header`;

    return (
        <div className={(props.className ? props.className : '')} onClick={(e) => {
            if (props.AjaxLoadURL && !cats?.length > 0) {
                setIsLoading(true);
                lazyLoadItems(props.AjaxLoadURL);
            }
        }}>
            <Accordion defaultExpanded={props.defaultExpanded} disableGutters>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`panel-${toSlug(props.accordionTitle)}-content`}
                    id={accordionId}
                    className="accordion-header"
                >
                    {props.accordionTitle}
                </AccordionSummary>

                <AccordionDetails>
                    {props.mapDetails && !cats?.length > 0 &&
                        <>
                            <div className='accordion-columns'>
                                {cats &&
                                    <ul className='accordion__container'>
                                        {createDrilldown(`dd-${props.accordionTitle}`, cats, 4)}
                                    </ul>
                                }
                            </div>
                            {props.viewAllRoute &&
                                <div className='accordion__btn-container'>
                                    <a className='ts-button btn accordion__btn' href={props.viewAllRoute}><TranslationText tKey='sandhills.tradepubwebsite.mvc.viewall' /></a>
                                </div>}
                        </>
                    }
                    {cats?.length > 0 &&
                        <>
                            <div className='accordion-columns'>
                                <ul className='accordion__container'>
                                    {createDrilldown(`pac-dd-${cats.ListingTitle}`, cats, null, false)}
                                </ul>
                            </div>
                            {props.viewAllRoute &&
                                <div className='accordion__btn-container'>
                                    <a className='ts-button btn accordion__btn' href={props.viewAllRoute}><TranslationText tKey='sandhills.tradepubwebsite.mvc.viewall' /></a>
                                </div>}
                        </>}
                    {!isLoading && !cats?.length > 0 &&
                        <div className='no-results' style={{'textAlign':'center', 'marginTop': '15px' }}><strong>No Listings Found</strong></div>
                    }
                    {isLoading &&
                        <CircularProgress sx={{ 'alignItems': 'center', 'display': 'inline-block', 'marginLeft': '48%', 'marginTop': '20px' }} size={40} />
                    }
                </AccordionDetails>
            </Accordion>
        </div>
    );
};

export default SandhillsAccordion;